import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useSelector } from 'react-redux';
import useTranslation from '../hooks/useTranslation';
import Button from '../components/Button';
import { FieldGroup, InputGroup, TextareaGroup } from '../components/Input';
import StreamerAssignmentManager from '../components/StreamerAssignmentManager';
import StartStreamV2 from '../components/modals/StartStreamV2';
import { Bracket as TournamentBracket } from './TournamentDetails';
import ENV_CONFIG from '../config/environment-unified';

const ThemedSelect = styled.select`
  position: relative;
  width: 100%;
  padding: 10px 15px;
  border-radius: 5px;
  background-color: ${({ theme }) => theme.color.secondaryBg};
  color: ${({ theme }) => theme.color.darkerText};
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 1.5;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    box-shadow: 0 0 0 0.2rem rgba(69, 105, 255, 0.8);
    background-color: ${({ theme }) => theme.color.secondaryBg};
    outline: 0;
  }
`;

const BracketEmbed = styled.div`
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#2D3748' : theme.color.border)};
  padding: 16px;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#0f172a' : theme.color.background)};
  margin-bottom: 24px;
`;

const BracketControlInput = styled.input`
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#2D3748' : theme.color.border)};
  background: ${({ theme }) => (theme.mode === 'dark' ? '#0b1220' : theme.color.secondaryBg)};
  color: ${({ theme }) => theme.color.darkerText};
  outline: 0;

  &:focus {
    box-shadow: 0 0 0 0.2rem rgba(69, 105, 255, 0.6);
  }
`;

const BracketControlSelect = styled.select`
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#2D3748' : theme.color.border)};
  background: ${({ theme }) => (theme.mode === 'dark' ? '#0f172a' : theme.color.secondaryBg)};
  color: ${({ theme }) => theme.color.darkerText};
  outline: 0;

  &:focus {
    box-shadow: 0 0 0 0.2rem rgba(69, 105, 255, 0.6);
  }
`;

const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  min-height: 100vh;
`;

const Header = styled.div`
  background: ${({ theme }) => theme.cardBg};
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 30px;
  border: 1px solid ${({ theme }) => theme.borderColor};
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.color.primary};
  margin: 0 0 10px 0;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.textSecondary};
  margin: 5px 0;
  font-size: 0.9rem;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  border-bottom: 2px solid ${({ theme }) => theme.color.border};
`;

const Tab = styled.button`
  background: ${({ active, theme }) => (active ? theme.color.secondaryBg : 'transparent')};
  color: ${({ active, theme }) => (active ? theme.color.primaryText : theme.color.lighterText)};
  border: none;
  padding: 15px 25px;
  cursor: pointer;
  font-size: 1rem;
  border-bottom: 3px solid ${({ active, theme }) => (active ? theme.color.primary : 'transparent')};
  transition: all 0.2s;
  
  &:hover {
    color: ${({ theme }) => theme.color.primaryText};
    background: ${({ theme }) => theme.color.secondaryBg};
  }
`;

const ContentCard = styled.div`
  background: ${({ theme }) => theme.cardBg};
  border-radius: 12px;
  padding: 25px;
  border: 1px solid ${({ theme }) => theme.borderColor};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.borderColor};
  }
  
  th {
    color: ${({ theme }) => theme.color.primary};
    font-weight: 600;
  }
  
  td {
    color: ${({ theme }) => theme.textColor};
  }
  
  tr:hover {
    background: ${({ theme }) => theme.hoverBg};
  }
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
  background: ${props => {
    switch (props.status) {
      case 'completed': return '#6B7280';
      // Event streamer invitation statuses
      case 'inactive': return '#F59E0B'; // invited/pending
      case 'active': return '#10B981'; // accepted
      case 'removed': return '#EF4444'; // declined/removed

      case 'pending': return '#FFA500';
      case 'accepted': return '#10B981';
      case 'rejected': return '#EF4444';
      case 'approved': return '#10B981';
      default: return '#666';
    }
  }};
  color: white;
`;

const formatStatusLabel = (status) => {
  switch (status) {
    // Event streamer invitation statuses (eventStreamer.status)
    case 'inactive': return 'pending';
    case 'active': return 'accepted';
    case 'removed': return 'declined';
    default: return status;
  }
};

const ActionButton = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-size: 0.85rem;
  margin-right: 5px;
  background: ${props => props.variant === 'danger' ? '#EF4444' : '#3182ce'};
  color: white;
  
  &:hover {
    opacity: 0.8;
  }
  
  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
`;

const EventDashboard = () => {
  const { slug } = useParams();
  const history = useHistory();
  const isLoggedIn = useSelector(state => state.user.isLoggedIn);
  const user = useSelector(state => state.user.data);
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState('overview');
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewers, setViewers] = useState([]);
  const [streamers, setStreamers] = useState([]);
  const [showGoLiveModal, setShowGoLiveModal] = useState(false);
  const [ticketStats, setTicketStats] = useState(null);
  const [generatingCodes, setGeneratingCodes] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState([]);
  const [issuedCodes, setIssuedCodes] = useState([]);
  const [tournamentPlayers, setTournamentPlayers] = useState([]);
  const [tournamentInfo, setTournamentInfo] = useState(null);
  const [loadingTournamentAdmin, setLoadingTournamentAdmin] = useState(false);
  const [tournamentMatches, setTournamentMatches] = useState([]);
  const [settingWinner, setSettingWinner] = useState(null);
  const [tournamentTeams, setTournamentTeams] = useState([]);
  const [unassignedPlayers, setUnassignedPlayers] = useState([]);
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [teamAssigning, setTeamAssigning] = useState(false);
  const [selectedAssign, setSelectedAssign] = useState({}); // teamId -> userId
  const [teamSizeInput, setTeamSizeInput] = useState(2);
  const [tournamentSizeInput, setTournamentSizeInput] = useState(32);
  const [updatingTournamentSize, setUpdatingTournamentSize] = useState(false);
  const [resettingTournament, setResettingTournament] = useState(false);
  const [deletingEvent, setDeletingEvent] = useState(false);

  // Get event category
  const eventCategory = event?.category?.slug || '';
  const isTournament = eventCategory === 'tournament';
  const isStream = eventCategory === 'stream';
  const isWorkshop = eventCategory === 'workshop';

  // Fetch event data
  useEffect(() => {
    if (!isLoggedIn) {
      history.replace("/?noAuth");
      return;
    }
    fetchEventData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isLoggedIn]);

  // Preload tournament dashboard data so "Spectators" + "Players/Streamers" are ready without clicking tabs.
  useEffect(() => {
    if (!event?.id) return;
    if (!isTournament) return;
    // Load spectators + streamers (invitations) + tournament admin (approved players/teams) eagerly
    fetchViewers();
    fetchStreamers();
    fetchTournamentAdminData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id, isTournament]);

  const fetchEventData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/events/${slug}`, {
        withCredentials: true
      });
      setEvent(response.data.event);

      // Check if user is the organizer
      if (response.data.event.createdBy.toString() !== user?.id?.toString()) {
        toast.error(t('eventdashboard.not_authorized_manage', 'You are not authorized to manage this event'));
        history.push(`/events/${slug}`);
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      if (error?.response?.status === 401) {
        history.replace("/?noAuth");
        return;
      }
      toast.error(t('eventdashboard.failed_to_load_event', 'Failed to load event'));
    } finally {
      setLoading(false);
    }
  };

  const fetchViewers = async () => {
    try {
      const response = await axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/${slug}/participants`, {
        withCredentials: true
      });
      setViewers(response.data.participants || []);
    } catch (error) {
      console.error('Error fetching viewers:', error);
    }
  };

  const fetchTicketStats = async () => {
    if (!event?.id) return;
    try {
      const response = await axios.get(`${ENV_CONFIG.TICKET_EVENT_ENDPOINT}/${event.id}`, {
        withCredentials: true
      });
      setTicketStats(response.data.stats || null);
    } catch (error) {
      console.error('Error fetching ticket stats:', error);
    }
  };

  const fetchIssuedTicketCodes = async () => {
    if (!event?.id || !user?.id) return;
    try {
      const res = await axios.get(
        `${ENV_CONFIG.TICKET_CODES_ENDPOINT}/${event.id}/codes`,
        { params: { organizerId: user.id.toString() }, withCredentials: true }
      );
      setIssuedCodes(res.data?.codes || []);
    } catch (e) {
      console.error('Error fetching issued ticket codes:', e);
      setIssuedCodes([]);
    }
  };

  const fetchStreamers = async () => {
    try {
      const response = await axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/${slug}/streamers`, {
        withCredentials: true
      });
      setStreamers(response.data.streamers || []);
    } catch (error) {
      console.error('Error fetching streamers:', error);
    }
  };

  const fetchTournamentAdminData = async () => {
    if (!event?.tournamentId || !isTournament) return;
    try {
      setLoadingTournamentAdmin(true);

      // Fetch players for this tournament (API returns empty array if none)
      const playersRes = await axios.get(`${ENV_CONFIG.GET_PLAYERS_ENDPOINT}${event.tournamentId}`, {
        withCredentials: true
      });
      setTournamentPlayers(playersRes.data || []);

      // Fetch basic tournament info (size, active, bracket data)
      const infoRes = await axios.get(`${ENV_CONFIG.TOURNAMENT_ENDPOINT}/getTournament/${event.tournamentId}`, {
        withCredentials: true
      });
      setTournamentInfo(infoRes.data);
      // Prefill max players input from tournament size
      if (infoRes.data?.size) {
        setTournamentSizeInput(String(infoRes.data.size));
      }

      // Fetch bracket/matches data if tournament is active
      if (infoRes.data?.Active || infoRes.data?.active) {
        try {
          // Tournament service route: GET /tournament/:id/bracket (proxied as /api/tournament/:id/bracket)
          const bracketRes = await axios.get(`${ENV_CONFIG.TOURNAMENT_ENDPOINT}/${event.tournamentId}/bracket`, {
            withCredentials: true
          });
          // Extract matches from bracket data
          const brackets = bracketRes.data?.bracket || [];
          const allMatches = brackets.flatMap(round => round.seeds || []);
          setTournamentMatches(allMatches);
        } catch (bracketError) {
          console.log('No bracket data yet:', bracketError.message);
          setTournamentMatches([]);
        }
      } else {
        setTournamentMatches([]);
      }

      // Team tournaments: load teams + unassigned approved players
      if (infoRes.data?.isTeam || infoRes.data?.isTeam === true) {
        try {
          const [teamsRes, unassignedRes] = await Promise.all([
            axios.get(`${ENV_CONFIG.TOURNAMENT_ENDPOINT}/player/team/list/${event.tournamentId}`, { withCredentials: true }),
            axios.get(`${ENV_CONFIG.TOURNAMENT_ENDPOINT}/player/team/unassigned/${event.tournamentId}`, { withCredentials: true })
          ]);
          setTournamentTeams(teamsRes.data?.teams || []);
          setUnassignedPlayers(unassignedRes.data?.unassigned || []);
        } catch (e) {
          setTournamentTeams([]);
          setUnassignedPlayers([]);
        }
      } else {
        setTournamentTeams([]);
        setUnassignedPlayers([]);
      }
    } catch (error) {
      console.error('Error fetching tournament admin data:', error);
      toast.error(t('eventdashboard.failed_load_tournament_info', 'Failed to load tournament info'));
    } finally {
      setLoadingTournamentAdmin(false);
    }
  };

  const handleUpdateTournamentSize = async () => {
    if (!event?.tournamentId) return;
    if (tournamentInfo?.active || tournamentInfo?.Active) {
      toast.error(t('eventdashboard.tournament_active_reset_to_change_max_players', 'Tournament already active. Reset tournament to change max players.'));
      return;
    }

    const size = parseInt(tournamentSizeInput, 10);
    if (isNaN(size) || size < 2) {
      toast.error(t('eventdashboard.max_players_min_2', 'Max players must be at least 2'));
      return;
    }

    try {
      setUpdatingTournamentSize(true);
      const fd = new FormData();
      fd.append('size', String(size));

      await axios.put(`${ENV_CONFIG.UPDATE_TOURNAMENT_ENDPOINT}${event.tournamentId}`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(t('eventdashboard.max_players_updated', 'Max players updated'));
      fetchTournamentAdminData();
    } catch (e) {
      console.error('Error updating tournament size:', e);
      toast.error(e.response?.data?.errorMessage || t('eventdashboard.failed_update_max_players', 'Failed to update max players'));
    } finally {
      setUpdatingTournamentSize(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'viewers') {
      fetchViewers();
    } else if (activeTab === 'streamers') {
      fetchStreamers();
    } else if (activeTab === 'settings' && event?.ticketPrice > 0) {
      fetchTicketStats();
      fetchIssuedTicketCodes();
    } else if (activeTab === 'bracket' && isTournament) {
      fetchTournamentAdminData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleBanUser = async (userId) => {
    if (!window.confirm(t('eventdashboard.confirm_ban_user', 'Are you sure you want to ban this user?'))) return;

    try {
      await axios.post(`${ENV_CONFIG.EVENTS_ENDPOINT}/${slug}/ban`,
        { userId },
        { withCredentials: true }
      );
      toast.success(t('eventdashboard.user_banned_success', 'User banned successfully'));
      if (activeTab === 'viewers') fetchViewers();
    } catch (error) {
      toast.error(t('eventdashboard.failed_ban_user', 'Failed to ban user'));
    }
  };

  // Tournament player approvals are handled by players accepting/declining invites.

  const handleRemoveStreamer = async (streamerId) => {
    if (!window.confirm(t('eventdashboard.confirm_remove_streamer', 'Remove this streamer from the event?'))) return;

    try {
      await axios.delete(`${ENV_CONFIG.EVENTS_ENDPOINT}/${slug}/streamers/${streamerId}`, {
        withCredentials: true
      });
      toast.success(t('eventdashboard.streamer_removed', 'Streamer removed'));
      fetchStreamers();
    } catch (error) {
      toast.error(t('eventdashboard.failed_remove_streamer', 'Failed to remove streamer'));
    }
  };

  const handleUpdateEvent = async (updates) => {
    try {
      await axios.put(`${ENV_CONFIG.EVENTS_ENDPOINT}/${slug}`, updates, {
        withCredentials: true
      });
      toast.success(t('eventdashboard.event_updated_success', 'Event updated successfully'));
      fetchEventData();
    } catch (error) {
      toast.error(t('eventdashboard.failed_update_event', 'Failed to update event'));
    }
  };

  const handleGenerateTicketCodes = async () => {
    if (!event?.id || !user?.id) return;

    const qtyInput = window.prompt(t('eventdashboard.prompt_ticket_codes_qty', 'How many ticket codes do you want to generate?'), '10');
    if (!qtyInput) return;
    const quantity = parseInt(qtyInput, 10);
    if (isNaN(quantity) || quantity <= 0) {
      toast.error(t('eventdashboard.invalid_quantity', 'Invalid quantity'));
      return;
    }

    try {
      setGeneratingCodes(true);
      setGeneratedCodes([]);

      const response = await axios.post(
        `${ENV_CONFIG.TICKET_GENERATE_ENDPOINT}/${event.id}/generate-codes`,
        {
          organizerId: user.id.toString(),
          quantity,
          // Must match DB constraint (tickets_ticket_type_check)
          ticketType: 'pass_day',
          price: event.ticketPrice || 0
        },
        { withCredentials: true }
      );

      setGeneratedCodes(response.data.codes || []);
      toast.success(`${t('eventdashboard.generated', 'Generated')} ${response.data.count || 0} ${t('eventdashboard.codes', 'codes')}`);
      fetchTicketStats();
      fetchIssuedTicketCodes();
    } catch (error) {
      console.error('Error generating ticket codes:', error);
      toast.error(error.response?.data?.message || t('eventdashboard.failed_generate_ticket_codes', 'Failed to generate ticket codes'));
    } finally {
      setGeneratingCodes(false);
    }
  };

  const handleApproveTournamentPlayer = async (playerId) => {
    if (!event?.tournamentId) return;
    try {
      await axios.put(
        ENV_CONFIG.APPROVE_PLAYER_ENDPOINT,
        { tournmentID: event.tournamentId, PlayerID: playerId },
        { withCredentials: true }
      );
      toast.success(t('eventdashboard.player_approved', 'Player approved'));
      fetchTournamentAdminData();
    } catch (error) {
      console.error('Error approving player:', error);
      toast.error(t('eventdashboard.failed_approve_player', 'Failed to approve player'));
    }
  };

  const handleRemoveTournamentPlayer = async (playerId) => {
    if (!window.confirm(t('eventdashboard.confirm_remove_player', 'Remove this player from the tournament?'))) return;
    try {
      await axios.delete(`${ENV_CONFIG.PLAYER_ENDPOINT}${playerId}`, {
        withCredentials: true
      });
      toast.success(t('eventdashboard.player_removed_from_tournament', 'Player removed from tournament'));
      fetchTournamentAdminData();
    } catch (error) {
      console.error('Error removing player:', error);
      toast.error(t('eventdashboard.failed_remove_player', 'Failed to remove player'));
    }
  };

  const handleStartTournament = async () => {
    if (!event?.tournamentId) return;
    try {
      await axios.post(
        ENV_CONFIG.TOURNAMENT_START_ENDPOINT,
        { tournmentID: event.tournamentId },
        { withCredentials: true }
      );
      toast.success(t('eventdashboard.tournament_started', 'Tournament started'));
      fetchTournamentAdminData();
    } catch (error) {
      console.error('Error starting tournament:', error);
      toast.error(error.response?.data?.errorMessage || t('eventdashboard.failed_start_tournament', 'Failed to start tournament'));
    }
  };

  const handleEnableTeams = async () => {
    if (!event?.tournamentId) return;
    const size = parseInt(teamSizeInput, 10);
    if (isNaN(size) || size < 1) {
      toast.error(t('eventdashboard.team_size_min_1', 'Team size must be at least 1'));
      return;
    }
    try {
      await axios.post(
        `${ENV_CONFIG.TOURNAMENT_ENDPOINT}/team-config/${event.tournamentId}`,
        { isTeam: true, teamSize: size },
        { withCredentials: true }
      );
      toast.success(t('eventdashboard.teams_enabled', 'Teams enabled'));
      fetchTournamentAdminData();
    } catch (e) {
      console.error('Error enabling teams:', e);
      toast.error(e.response?.data?.errorMessage || t('eventdashboard.failed_enable_teams', 'Failed to enable teams'));
    }
  };

  const handleUpdateTeamSize = async () => {
    if (!event?.tournamentId) return;
    const size = parseInt(teamSizeInput, 10);
    if (isNaN(size) || size < 1) {
      toast.error(t('eventdashboard.team_size_min_1', 'Team size must be at least 1'));
      return;
    }
    try {
      await axios.post(
        `${ENV_CONFIG.TOURNAMENT_ENDPOINT}/team-config/${event.tournamentId}`,
        { isTeam: true, teamSize: size },
        { withCredentials: true }
      );
      toast.success(t('eventdashboard.team_size_updated', 'Team size updated'));
      fetchTournamentAdminData();
    } catch (e) {
      console.error('Error updating team size:', e);
      toast.error(e.response?.data?.errorMessage || t('eventdashboard.failed_update_team_size', 'Failed to update team size'));
    }
  };

  const handleDisableTeams = async () => {
    if (!event?.tournamentId) return;
    if (!window.confirm(t('eventdashboard.confirm_disable_teams', 'Disable teams? (You can re-enable later. Existing teams will remain in DB for now.)'))) return;
    try {
      await axios.post(
        `${ENV_CONFIG.TOURNAMENT_ENDPOINT}/team-config/${event.tournamentId}`,
        { isTeam: false, teamSize: 0 },
        { withCredentials: true }
      );
      toast.success(t('eventdashboard.teams_disabled_success', 'Teams disabled'));
      fetchTournamentAdminData();
    } catch (e) {
      console.error('Error disabling teams:', e);
      toast.error(e.response?.data?.errorMessage || t('eventdashboard.failed_disable_teams', 'Failed to disable teams'));
    }
  };

  const handleResetTournament = async () => {
    if (!event?.tournamentId) return;
    if (!window.confirm(t('eventdashboard.confirm_reset_tournament', 'Reset tournament? This will clear the bracket/matches and allow re-configuring teams.'))) return;
    try {
      setResettingTournament(true);
      await axios.post(
        `${ENV_CONFIG.TOURNAMENT_ENDPOINT}/reset/${event.tournamentId}`,
        {},
        { withCredentials: true }
      );
      toast.success(t('eventdashboard.tournament_reset', 'Tournament reset'));
      fetchTournamentAdminData();
    } catch (e) {
      console.error('Error resetting tournament:', e);
      toast.error(e.response?.data?.errorMessage || t('eventdashboard.failed_reset_tournament', 'Failed to reset tournament'));
    } finally {
      setResettingTournament(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!event?.slug) return;

    const confirm1 = window.confirm(
      isTournament
        ? t('eventdashboard.delete_confirm_tournament', 'Delete this tournament? This cannot be undone.')
        : t('eventdashboard.delete_confirm_event', 'Delete this event? This cannot be undone.')
    );
    if (!confirm1) return;

    try {
      setDeletingEvent(true);
      await axios.delete(`${ENV_CONFIG.EVENTS_ENDPOINT}/events/${slug}`, {
        withCredentials: true
      });
      toast.success(
        t('eventdashboard.deleted_success', 'Deleted successfully')
      );
      history.push(isTournament ? '/events/tournaments' : '/events');
    } catch (e) {
      console.error('Error deleting event:', e);
      toast.error(
        e.response?.data?.error ||
        t('eventdashboard.deleted_error', 'Failed to delete')
      );
    } finally {
      setDeletingEvent(false);
    }
  };

  const handleSyncTournamentPlayers = async () => {
    try {
      await axios.post(
        `${ENV_CONFIG.EVENTS_ENDPOINT}/events/${slug}/tournament/sync-players`,
        {},
        { withCredentials: true }
      );
      toast.success(t('eventdashboard.synced_players_to_tournament', 'Synced players to tournament'));
      fetchTournamentAdminData();
    } catch (error) {
      console.error('Error syncing tournament players:', error);
      toast.error(error.response?.data?.error || t('eventdashboard.failed_sync_players', 'Failed to sync players'));
    }
  };

  const handleCreateTeam = async () => {
    if (!event?.tournamentId) return;
    const name = (newTeamName || '').trim();
    if (!name) return;
    try {
      setCreatingTeam(true);
      await axios.post(
        `${ENV_CONFIG.TOURNAMENT_ENDPOINT}/player/team/create`,
        { tournmentID: event.tournamentId, name },
        { withCredentials: true }
      );
      setNewTeamName('');
      toast.success(t('eventdashboard.team_created', 'Team created'));
      fetchTournamentAdminData();
    } catch (e) {
      console.error('Error creating team:', e);
      toast.error(e.response?.data?.errorMessage || t('eventdashboard.failed_create_team', 'Failed to create team'));
    } finally {
      setCreatingTeam(false);
    }
  };

  const handleAssignToTeam = async (teamId) => {
    if (!event?.tournamentId) return;
    const userId = selectedAssign[teamId];
    if (!userId) return;
    try {
      setTeamAssigning(true);
      await axios.put(
        `${ENV_CONFIG.TOURNAMENT_ENDPOINT}/player/addPlayerToTeam`,
        { tournmentID: event.tournamentId, teamID: teamId, userId },
        { withCredentials: true }
      );
      toast.success(t('eventdashboard.player_assigned_to_team', 'Player assigned to team'));
      setSelectedAssign((prev) => ({ ...prev, [teamId]: '' }));
      fetchTournamentAdminData();
    } catch (e) {
      console.error('Error assigning to team:', e);
      toast.error(e.response?.data?.errorMessage || t('eventdashboard.failed_assign_player', 'Failed to assign player'));
    } finally {
      setTeamAssigning(false);
    }
  };

  const handleRemoveFromTeam = async (teamId, userId) => {
    if (!event?.tournamentId) return;
    try {
      await axios.post(
        `${ENV_CONFIG.TOURNAMENT_ENDPOINT}/player/team/remove-member`,
        { tournmentID: event.tournamentId, teamId, userId },
        { withCredentials: true }
      );
      toast.success(t('eventdashboard.member_removed', 'Member removed'));
      fetchTournamentAdminData();
    } catch (e) {
      console.error('Error removing member:', e);
      toast.error(e.response?.data?.errorMessage || t('eventdashboard.failed_remove_member', 'Failed to remove member'));
    }
  };

  const [deletingTeamId, setDeletingTeamId] = useState(null);

  const handleDeleteTeam = async (team) => {
    if (!event?.tournamentId || !team?.id) return;

    const isRunning = !!(tournamentInfo?.active || tournamentInfo?.Active);
    const membersCount = Array.isArray(team.team) ? team.team.length : 0;

    if (isRunning) {
      toast.error(t('eventdashboard.team_delete_running', 'Tournament is running'));
      return;
    }

    const teamLabel = team.username || t('eventdashboard.team', 'Team');
    const ok = window.confirm(
      membersCount > 0
        ? `${t('eventdashboard.delete_team', 'Delete team')} "${teamLabel}"?\n\n${t('eventdashboard.delete_team_will_remove_members', 'This team has members. We will remove all members first, then delete the team.')}\n${t('eventdashboard.cannot_undo', 'This cannot be undone.')}`
        : `${t('eventdashboard.delete_team', 'Delete team')} "${teamLabel}"? ${t('eventdashboard.cannot_undo', 'This cannot be undone.')}`
    );
    if (!ok) return;

    try {
      setDeletingTeamId(team.id);

      // If the team has members, remove them first (backend only allows deleting empty teams).
      if (membersCount > 0) {
        const memberUsers = Array.isArray(team.memberUsers) ? team.memberUsers : [];
        // Fallback: sometimes we only have `team.team` (IDs or objects)
        const fallbackMembers = Array.isArray(team.team) ? team.team : [];

        const memberIds = (memberUsers.length > 0
          ? memberUsers.map((m) => m?.id).filter(Boolean)
          : fallbackMembers
              .map((m) => (typeof m === 'object' ? m?.id : m))
              .filter(Boolean)
        );

        for (const userId of memberIds) {
          await axios.post(
            `${ENV_CONFIG.TOURNAMENT_ENDPOINT}/player/team/remove-member`,
            { tournmentID: event.tournamentId, teamId: team.id, userId },
            { withCredentials: true }
          );
        }
      }

      try {
        // Preferred endpoint (team-specific)
        await axios.delete(
          `${ENV_CONFIG.TOURNAMENT_ENDPOINT}/player/team/${team.id}`,
          { withCredentials: true }
        );
      } catch (err) {
        // Backward-compatibility: some deployments may not expose /player/team/:teamId
        // In that case, fall back to deleting the player record (backend restricts team deletion to empty teams).
        const status = err?.response?.status;
        if (status === 404) {
          await axios.delete(
            `${ENV_CONFIG.TOURNAMENT_ENDPOINT}/player/${team.id}`,
            { withCredentials: true }
          );
        } else {
          throw err;
        }
      }
      toast.success(t('eventdashboard.team_deleted_success', 'Team deleted'));
      fetchTournamentAdminData();
    } catch (e) {
      console.error('Error deleting team:', e);
      toast.error(e.response?.data?.errorMessage || t('eventdashboard.team_deleted_error', 'Failed to delete team'));
    } finally {
      setDeletingTeamId(null);
    }
  };

  const handleSetWinner = async (matchId, winnerId, score1 = '0', score2 = '0') => {
    if (!event?.tournamentId || !matchId || !winnerId) return;
    try {
      setSettingWinner(matchId);
      await axios.post(
        ENV_CONFIG.SET_WINNER_ENDPOINT,
        {
          tournmentID: event.tournamentId,
          matchID: matchId,
          winnerID: winnerId,
          scorePlayer1: score1,
          scorePlayer2: score2
        },
        { withCredentials: true }
      );
      toast.success(t('eventdashboard.winner_set_success', 'Winner set successfully'));
      fetchTournamentAdminData();
    } catch (error) {
      console.error('Error setting winner:', error);
      toast.error(error.response?.data?.errorMessage || t('eventdashboard.failed_set_winner', 'Failed to set winner'));
    } finally {
      setSettingWinner(null);
    }
  };

  if (loading) {
    return <DashboardContainer><p>{t('common.loading', 'Loading...')}</p></DashboardContainer>;
  }

  if (!event) {
    return <DashboardContainer><p>{t('eventdashboard.event_not_found', 'Event not found')}</p></DashboardContainer>;
  }

  return (
    <DashboardContainer>
      <Header>
        <Title>{event.name}</Title>
        <Subtitle>{t('eventdashboard.organizer', 'Organizer')}: {event.organizerUsername || t('eventdashboard.you', 'You')}</Subtitle>
        <Subtitle>{t('eventdashboard.category', 'Category')}: {event.category?.name}</Subtitle>
        <Subtitle>{t('eventdashboard.status', 'Status')}: <StatusBadge status={event.status}>{event.status}</StatusBadge></Subtitle>
      </Header>

      <TabContainer>
        <Tab active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
          📊 {t('eventdashboard.tab_overview', 'Overview')}
        </Tab>

        {/* Viewers tab - Show for all event types */}
        <Tab active={activeTab === 'viewers'} onClick={() => setActiveTab('viewers')}>
          👥 {isTournament ? t('eventdashboard.spectators', 'Spectators') : t('eventdashboard.viewers', 'Viewers')} ({viewers.length})
        </Tab>

        {/* Streamers tab - Only for Tournament (players who stream) */}
        {isTournament && (
          <>
            <Tab active={activeTab === 'streamers'} onClick={() => setActiveTab('streamers')}>
              🎥 {t('eventdashboard.players_streamers', 'Players/Streamers')} ({streamers.length})
            </Tab>
            <Tab active={activeTab === 'bracket'} onClick={() => setActiveTab('bracket')}>
              🌳 {t('eventdashboard.bracket', 'Bracket')}
            </Tab>
          </>
        )}

        {/* Stream Control tab - Only for Stream and Workshop (organizer is the streamer) */}
        {(isStream || isWorkshop) && (
          <Tab active={activeTab === 'stream'} onClick={() => setActiveTab('stream')}>
            📺 {t('eventdashboard.stream_control', 'Stream Control')}
          </Tab>
        )}

        <Tab active={activeTab === 'settings'} onClick={() => setActiveTab('settings')}>
          ⚙️ {t('eventdashboard.settings', 'Settings')}
        </Tab>
      </TabContainer>

      <ContentCard>
        {activeTab === 'overview' && (
          <div>
            {/* Stats Grid */}
            <StatsGrid>
              <StatBox>
                <StatIcon>👥</StatIcon>
                <StatContent>
                  <StatValue>{viewers.length}</StatValue>
                  <StatLabel>
                    {isTournament
                      ? t('eventdashboard.spectators', 'Spectators')
                      : isWorkshop
                        ? t('eventdashboard.attendees', 'Attendees')
                        : t('eventdashboard.viewers', 'Viewers')}
                  </StatLabel>
                </StatContent>
              </StatBox>

              {/* Show streamers count only for Tournament */}
              {isTournament && (
                <StatBox>
                  <StatIcon>🎥</StatIcon>
                  <StatContent>
                    <StatValue>{streamers.length}</StatValue>
                    <StatLabel>{t('eventdashboard.players_streamers', 'Players/Streamers')}</StatLabel>
                  </StatContent>
                </StatBox>
              )}

              {/* Show stream status for Stream/Workshop */}
              {(isStream || isWorkshop) && (
                <StatBox>
                  <StatIcon>{event.status === 'active' ? '🔴' : '⚫'}</StatIcon>
                  <StatContent>
                    <StatValue>{event.status === 'active' ? 'LIVE' : 'OFFLINE'}</StatValue>
                    <StatLabel>{t('eventdashboard.stream_status', 'Stream Status')}</StatLabel>
                  </StatContent>
                </StatBox>
              )}

              <StatBox>
                <StatIcon>💰</StatIcon>
                <StatContent>
                  <StatValue>{event.ticketPrice || 0}</StatValue>
                  <StatLabel>{event.ticketPrice === 0 ? t('eventdashboard.free_event', 'Free Event') : t('eventdashboard.gcoins_per_ticket', 'G-Coins/Ticket')}</StatLabel>
                </StatContent>
              </StatBox>

              <StatBox>
                <StatIcon>📊</StatIcon>
                <StatContent>
                  <StatValue>{event.maxParticipants || '∞'}</StatValue>
                  <StatLabel>{isTournament ? t('eventdashboard.max_players', 'Max Players') : t('eventdashboard.max_capacity', 'Max Capacity')}</StatLabel>
                </StatContent>
              </StatBox>
            </StatsGrid>

            {/* Event Details Section */}
            <Section>
              <SectionHeader>
                <SectionIcon>📋</SectionIcon>
                <h3>{t('eventdashboard.event_details', 'Event Details')}</h3>
              </SectionHeader>
              <DetailGrid>
                <DetailItem>
                  <DetailLabel>{t('event.description', 'Description')}</DetailLabel>
                  <DetailValue>{event.description || t('component.no_description_provided', 'No description provided')}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>{t('event.startDate', 'Start date')}</DetailLabel>
                  <DetailValue>
                    📅 {new Date(event.startDate).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                    <br />
                    🕐 {new Date(event.startDate).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </DetailValue>
                </DetailItem>
                {event.endDate && (
                  <DetailItem>
                    <DetailLabel>{t('event.endDate', 'End date')}</DetailLabel>
                    <DetailValue>
                      📅 {new Date(event.endDate).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                      <br />
                      🕐 {new Date(event.endDate).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </DetailValue>
                  </DetailItem>
                )}
                <DetailItem>
                  <DetailLabel>{t('eventdashboard.event_type', 'Event Type')}</DetailLabel>
                  <DetailValue>
                    <CategoryBadge color={event.category?.color || '#4299e1'}>
                      {event.category?.icon} {event.category?.name}
                    </CategoryBadge>
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>{t('eventdashboard.visibility', 'Visibility')}</DetailLabel>
                  <DetailValue>
                    {event.isPublic ? t('eventdashboard.public_event', '🌍 Public Event') : t('eventdashboard.private_event', '🔒 Private Event')}
                  </DetailValue>
                </DetailItem>
                {event.gameId && (
                  <DetailItem>
                    <DetailLabel>{t('createevent.game', 'Game')}</DetailLabel>
                    <DetailValue>🎮 {event.gameName || event.gameId}</DetailValue>
                  </DetailItem>
                )}
                {event.tags && event.tags.length > 0 && (
                  <DetailItem>
                    <DetailLabel>{t('createevent.tags', 'Tags')}</DetailLabel>
                    <DetailValue>
                      <TagsContainer>
                        {event.tags.map((tag, idx) => (
                          <Tag key={idx}>{tag}</Tag>
                        ))}
                      </TagsContainer>
                    </DetailValue>
                  </DetailItem>
                )}
              </DetailGrid>
            </Section>

            {/* Event Options Section */}
            {event.options && Object.keys(event.options).length > 0 && (
              <Section>
                <SectionHeader>
                  <SectionIcon>⚙️</SectionIcon>
                  <h3>{t('eventdashboard.event_configuration', 'Event Configuration')}</h3>
                </SectionHeader>
                <OptionsGrid>
                  {event.options.restream && (
                    <OptionItem enabled={true}>
                      <OptionIcon>📡</OptionIcon>
                      <OptionText>{t('createevent.enable_restream', 'Enable Restream')}</OptionText>
                    </OptionItem>
                  )}
                  {event.options.autoReplay && (
                    <OptionItem enabled={true}>
                      <OptionIcon>🎬</OptionIcon>
                      <OptionText>{t('createevent.auto_generate_replay', 'Auto-Generate Replay')}</OptionText>
                    </OptionItem>
                  )}
                  {event.options.multiPov && (
                    <OptionItem enabled={true}>
                      <OptionIcon>📹</OptionIcon>
                      <OptionText>{t('createevent.multi_pov', 'Multi-POV (Multiple Streamers / Angles)')}</OptionText>
                    </OptionItem>
                  )}
                  {/* Auto-Highlight Clips option disabled for now */}
                  {event.options.chatMode && (
                    <OptionItem enabled={true}>
                      <OptionIcon>💬</OptionIcon>
                      <OptionText>{t('eventdashboard.chat', 'Chat')}: {event.options.chatMode}</OptionText>
                    </OptionItem>
                  )}
                </OptionsGrid>
              </Section>
            )}

            {/* Quick Actions */}
            <Section>
              <SectionHeader>
                <SectionIcon>🚀</SectionIcon>
                <h3>{t('eventdashboard.quick_actions', 'Quick Actions')}</h3>
              </SectionHeader>
              <QuickActionsGrid>
                <QuickActionButton onClick={() => window.open(`/events/${slug}`, '_blank')}>
                  <ActionIcon>👁️</ActionIcon>
                  <ActionText>{t('eventdashboard.view_public_page', 'View Public Page')}</ActionText>
                </QuickActionButton>

                {/* Tournament: Manage Players/Streamers */}
                {isTournament && (
                  <QuickActionButton onClick={() => setActiveTab('streamers')}>
                    <ActionIcon>🎥</ActionIcon>
                    <ActionText>{t('eventdashboard.manage_players', 'Manage Players')}</ActionText>
                  </QuickActionButton>
                )}

                {/* Stream/Workshop: Stream Control */}
                {(isStream || isWorkshop) && (
                  <QuickActionButton onClick={() => setActiveTab('stream')}>
                    <ActionIcon>📺</ActionIcon>
                    <ActionText>{t('eventdashboard.stream_control', 'Stream Control')}</ActionText>
                  </QuickActionButton>
                )}

                <QuickActionButton onClick={() => setActiveTab('viewers')}>
                  <ActionIcon>👥</ActionIcon>
                  <ActionText>
                    {isTournament
                      ? t('eventdashboard.view_spectators', 'View Spectators')
                      : isWorkshop
                        ? t('eventdashboard.view_attendees', 'View Attendees')
                        : t('eventdashboard.view_viewers', 'View Viewers')}
                  </ActionText>
                </QuickActionButton>

                <QuickActionButton onClick={() => setActiveTab('settings')}>
                  <ActionIcon>⚙️</ActionIcon>
                  <ActionText>{t('eventdashboard.edit_settings', 'Edit Settings')}</ActionText>
                </QuickActionButton>
              </QuickActionsGrid>
            </Section>
          </div>
        )}

        {activeTab === 'viewers' && (
          <div>
            <h3>
              {isTournament
                ? t('eventdashboard.spectators', 'Spectators')
                : isWorkshop
                  ? t('eventdashboard.attendees', 'Attendees')
                  : t('eventdashboard.viewers_ticket_holders', 'Viewers (Ticket Holders)')}
            </h3>
            <p style={{ color: '#888', marginBottom: '20px' }}>
              {isTournament
                ? t('eventdashboard.viewers_tournament_help', 'Viewers/spectators who registered for the event (players are managed in Players/Streamers & Bracket)')
                : isWorkshop
                  ? t('eventdashboard.attendees_workshop_help', 'Attendees who registered for the workshop')
                  : t('eventdashboard.viewers_stream_help', 'Viewers who have access to the stream')}
            </p>
            {viewers.length === 0 ? (
              <p style={{ color: '#888' }}>
                {isTournament
                  ? t('eventdashboard.no_spectators_yet', 'No spectators yet')
                  : isWorkshop
                    ? t('eventdashboard.no_attendees_yet', 'No attendees yet')
                    : t('eventdashboard.no_viewers_yet', 'No viewers yet')}
              </p>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <th>{t('common.username', 'Username')}</th>
                    <th>{t('common.joined', 'Joined')}</th>
                    <th>{t('eventdashboard.status', 'Status')}</th>
                    <th>{t('common.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {viewers.map(viewer => (
                    <tr key={viewer.id}>
                      <td>{viewer.username}</td>
                      <td>{new Date(viewer.registeredAt).toLocaleDateString()}</td>
                      <td><StatusBadge status={viewer.status}>{viewer.status}</StatusBadge></td>
                      <td>
                        {!isTournament && (
                          <ActionButton variant="danger" onClick={() => handleBanUser(viewer.userId)}>
                            {t('common.ban', 'Ban')}
                          </ActionButton>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        )}

        {activeTab === 'streamers' && (
          <div>
            <h3>{t('eventdashboard.streamers_players', 'Streamers (Players)')}</h3>
            <p style={{ color: '#888', marginBottom: '20px' }}>
              {t('eventdashboard.streamers_help', 'Invited players who can compete and stream their POV')}
            </p>

            <StreamerAssignmentManager
              eventSlug={slug}
              onAssign={() => fetchStreamers()}
            />

            {streamers.length === 0 ? (
              <p style={{ color: '#888', marginTop: '20px' }}>{t('eventdashboard.no_streamers_invited_yet', 'No streamers invited yet')}</p>
            ) : (
              <Table style={{ marginTop: '20px' }}>
                <thead>
                  <tr>
                    <th>{t('common.username', 'Username')}</th>
                    <th>{t('common.role', 'Role')}</th>
                    <th>{t('eventdashboard.invitation', 'Invitation')}</th>
                    <th>{t('eventdashboard.invited', 'Invited')}</th>
                    <th>{t('common.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {streamers.map(streamer => (
                    <tr key={streamer.id}>
                      <td>{streamer.username}</td>
                      <td>{streamer.role}</td>
                      <td><StatusBadge status={streamer.status}>{formatStatusLabel(streamer.status)}</StatusBadge></td>
                      <td>{new Date(streamer.assignedAt).toLocaleDateString()}</td>
                      <td>
                        <ActionButton variant="danger" onClick={() => handleRemoveStreamer(streamer.id)}>
                          {t('common.remove', 'Remove')}
                        </ActionButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        )}

        {/* Tournament Bracket tab for tournaments - embedded bracket + simple admin */}
        {activeTab === 'bracket' && isTournament && (
          <div>
            <h3>{t('eventdashboard.tournament_bracket', '🌳 Tournament Bracket')}</h3>
            <p style={{ color: '#888', marginBottom: '20px' }}>
              {t('eventdashboard.tournament_bracket_help', 'Live bracket and basic player management for this tournament event.')}
            </p>
            {event.tournamentId ? (
              <>
                <BracketEmbed>
                  <TournamentBracket tournamentId={event.tournamentId} />
                </BracketEmbed>

                <Section>
                  <SectionHeader>
                    <SectionIcon>👥</SectionIcon>
                    <h3>{t('eventdashboard.players_tournament_control', 'Players & Tournament Control')}</h3>
                  </SectionHeader>
                  {loadingTournamentAdmin ? (
                    <p style={{ color: '#888' }}>{t('eventdashboard.loading_players', 'Loading players...')}</p>
                  ) : (
                    <>
                      <p style={{ color: '#888', marginBottom: '12px' }}>
                        {tournamentInfo?.isTeam
                          ? <>{t('eventdashboard.teams_entrants', 'Teams (entrants)')}: <strong>{(tournamentTeams || []).length}</strong></>
                          : <>{t('eventdashboard.approved_players', 'Approved players')}: <strong>{(tournamentPlayers || []).filter(p => p.status === 'approved').length}</strong></>
                        }
                        {tournamentInfo?.size && <> / {tournamentInfo.size}</>}
                      </p>
                      {tournamentInfo && (
                        <p style={{ color: '#888', marginBottom: '12px' }}>
                          {t('eventdashboard.status', 'Status')}: <strong>{tournamentInfo.Active || tournamentInfo.active ? t('eventdashboard.running', 'Running') : t('eventdashboard.not_started', 'Not started')}</strong>
                          {' '}• {t('eventdashboard.format', 'Format')}: <strong>{tournamentInfo.structure || tournamentInfo.format || t('eventdashboard.single_elimination', 'single-elimination')}</strong>
                        </p>
                      )}

                      <div style={{ marginBottom: 14, padding: 12, border: '1px solid #2D3748', borderRadius: 12, background: 'rgba(255,255,255,0.03)' }}>
                        <h4 style={{ marginTop: 0, marginBottom: 8 }}>{t('eventdashboard.bracket_settings', 'Bracket Settings')}</h4>
                        <p style={{ color: '#888', marginTop: 0, marginBottom: 10 }}>
                          {t('eventdashboard.max_players_bracket_help', 'Max players (bracket size). Editable only before starting the tournament.')}
                        </p>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                          <BracketControlInput
                            type="number"
                            min="2"
                            value={tournamentSizeInput}
                            disabled={tournamentInfo?.active || tournamentInfo?.Active || updatingTournamentSize}
                            onChange={(e) => setTournamentSizeInput(e.target.value)}
                            style={{ width: 140 }}
                          />
                          <Button
                            variant="secondary"
                            size="small"
                            disabled={tournamentInfo?.active || tournamentInfo?.Active || updatingTournamentSize}
                            onClick={handleUpdateTournamentSize}
                          >
                            {updatingTournamentSize ? t('common.updating', 'Updating...') : t('eventdashboard.update_max_players', 'Update max players')}
                          </Button>
                          {(tournamentInfo?.active || tournamentInfo?.Active) && (
                            <span style={{ color: '#fbbf24', fontSize: 12 }}>
                              ({t('eventdashboard.reset_tournament_to_change', 'reset tournament to change this')})
                            </span>
                          )}
                        </div>
                      </div>

                      <div style={{ marginBottom: '16px' }}>
                        <Button
                          variant="primary"
                          disabled={
                            !tournamentInfo ||
                            tournamentInfo.Active ||
                            (tournamentPlayers || []).filter(p => p.status === 'approved').length === 0
                          }
                          onClick={handleStartTournament}
                        >
                          🚀 {t('eventdashboard.start_tournament', 'Start Tournament')}
                        </Button>
                        <Button
                          variant="secondary"
                          style={{ marginLeft: 10 }}
                          disabled={!event?.tournamentId}
                          onClick={handleSyncTournamentPlayers}
                        >
                          🔄 {t('eventdashboard.sync_players', 'Sync Players')}
                        </Button>
                        <Button
                          variant="secondary"
                          style={{ marginLeft: 10 }}
                          disabled={!event?.tournamentId || resettingTournament}
                          onClick={handleResetTournament}
                        >
                          {resettingTournament ? t('eventdashboard.resetting', 'Resetting...') : t('eventdashboard.reset_tournament_button', '🧹 Reset Tournament')}
                        </Button>
                      </div>

                      {(tournamentInfo?.isTeam || tournamentInfo?.isTeam === true) ? (
                        <div style={{ marginBottom: 18, padding: 14, border: '1px solid #2D3748', borderRadius: 12, background: 'rgba(255,255,255,0.03)' }}>
                          <h4 style={{ marginTop: 0 }}>{t('eventdashboard.teams', 'Teams')}</h4>
                          <p style={{ color: '#888' }}>
                            {t('eventdashboard.team_size', 'Team size')}: <strong>{tournamentInfo?.teamSize || 0}</strong> • {t('eventdashboard.one_team_per_player', 'One player can be in only one team.')}
                          </p>
                          <p style={{ color: '#9CA3AF', marginTop: 0 }}>
                            {t('eventdashboard.team_delete_help', 'Only empty teams can be deleted before the tournament starts.')}
                          </p>
                          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                            <BracketControlInput
                              type="number"
                              min="1"
                              value={teamSizeInput}
                              onChange={(e) => setTeamSizeInput(e.target.value)}
                              style={{ width: 120 }}
                            />
                            <Button
                              variant="secondary"
                              size="small"
                              disabled={tournamentInfo?.active || tournamentInfo?.Active}
                              onClick={handleUpdateTeamSize}
                            >
                              {t('eventdashboard.update_team_size', 'Update team size')}
                            </Button>
                            <Button
                              variant="secondary"
                              size="small"
                              disabled={tournamentInfo?.active || tournamentInfo?.Active}
                              onClick={handleDisableTeams}
                            >
                              {t('eventdashboard.disable_teams', 'Disable teams')}
                            </Button>
                            {(tournamentInfo?.active || tournamentInfo?.Active) && (
                              <span style={{ color: '#fbbf24', fontSize: 12 }}>
                                ({t('eventdashboard.reset_tournament_to_change_team_settings', 'reset tournament to change team settings')})
                              </span>
                            )}
                          </div>

                          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                            <BracketControlInput
                              value={newTeamName}
                              onChange={(e) => setNewTeamName(e.target.value)}
                              placeholder={t('eventdashboard.team_name', 'Team name')}
                              style={{ minWidth: 240 }}
                            />
                            <Button variant="secondary" size="small" disabled={creatingTeam || !newTeamName.trim()} onClick={handleCreateTeam}>
                              {creatingTeam ? t('eventdashboard.creating', 'Creating...') : t('eventdashboard.create_team', 'Create team')}
                            </Button>
                          </div>

                          {tournamentTeams.length === 0 ? (
                            <p style={{ color: '#888' }}>{t('eventdashboard.no_teams_help', 'No teams yet. Create a team above, then assign approved players.')}</p>
                          ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
                              {tournamentTeams.map((team) => {
                                const teamMax = Number(tournamentInfo?.teamSize) || 0;
                                const membersCount = Array.isArray(team.team) ? team.team.length : 0;
                                const isTeamFull = teamMax > 0 && membersCount >= teamMax;
                                const isRunning = !!(tournamentInfo?.active || tournamentInfo?.Active);
                                const canDeleteTeam = !isRunning && membersCount === 0;
                                const deleteHint = canDeleteTeam
                                  ? t('eventdashboard.team_empty_can_delete', 'Empty team — you can delete it.')
                                  : isRunning
                                    ? t('eventdashboard.team_delete_running_hint', 'Tournament is running — team deletion is disabled.')
                                    : t('eventdashboard.team_delete_not_empty_hint', 'Remove all members to be able to delete this team.');

                                return (
                                  <div
                                    key={team.id}
                                    style={{
                                      border: membersCount === 0 ? '1px solid rgba(239, 68, 68, 0.45)' : '1px solid #2D3748',
                                      borderRadius: 12,
                                      padding: 12,
                                      background: '#0b1220'
                                    }}
                                  >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                      <div style={{ fontWeight: 800, color: '#E5E7EB' }}>{team.username || t('eventdashboard.team', 'Team')}</div>
                                        {membersCount === 0 && (
                                          <span
                                            style={{
                                              fontSize: 11,
                                              fontWeight: 900,
                                              padding: '4px 8px',
                                              borderRadius: 999,
                                              border: '1px solid rgba(239, 68, 68, 0.45)',
                                              color: '#FCA5A5',
                                              background: 'rgba(239, 68, 68, 0.10)'
                                            }}
                                            title={t('eventdashboard.team_empty_badge_help', 'This team has no members yet.')}
                                          >
                                            {t('eventdashboard.team_empty_badge', 'Empty team')}
                                          </span>
                                        )}
                                      </div>
                                      <div style={{ color: '#9CA3AF', fontSize: 12 }}>
                                        {t('eventdashboard.members', 'Members')}: <strong>{Array.isArray(team.team) ? team.team.length : 0}</strong> / {tournamentInfo?.teamSize || 0}
                                      </div>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                                      <span
                                        title={
                                          isRunning
                                            ? t('eventdashboard.team_delete_running', 'Tournament is running')
                                            : (Array.isArray(team.team) && team.team.length > 0)
                                              ? t('eventdashboard.team_delete_not_empty', 'Cannot delete a non-empty team')
                                              : ''
                                        }
                                      >
                                        <Button
                                          variant="danger"
                                          size="small"
                                          disabled={deletingTeamId === team.id}
                                          onClick={() => handleDeleteTeam(team)}
                                        >
                                          {deletingTeamId === team.id ? t('eventdashboard.deleting', 'Deleting...') : t('eventdashboard.team_delete', 'Delete team')}
                                        </Button>
                                      </span>
                                    </div>
                                    <div style={{ marginTop: 6, color: '#9CA3AF', fontSize: 12 }}>
                                      {deleteHint}
                                    </div>

                                    <div style={{ marginTop: 10 }}>
                                      {(Array.isArray(team.memberUsers) && team.memberUsers.length > 0) ? (
                                        <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', color: '#E5E7EB' }}>
                                          {team.memberUsers.map((m) => (
                                            <li key={String(m.id)} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', padding: '6px 0' }}>
                                              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                                {m.avatar ? (
                                                  <img
                                                    src={m.avatar}
                                                    alt={m.username}
                                                    style={{ width: 26, height: 26, borderRadius: 999, objectFit: 'cover' }}
                                                  />
                                                ) : null}
                                                <span style={{ fontWeight: 700 }}>{m.username}</span>
                                              </div>
                                              <button
                                                onClick={() => handleRemoveFromTeam(team.id, m.id)}
                                                style={{ background: 'transparent', border: 'none', color: '#EF4444', cursor: 'pointer', fontWeight: 700 }}
                                              >
                                          {t('common.remove', 'Remove')}
                                              </button>
                                            </li>
                                          ))}
                                        </ul>
                                      ) : (
                                        <div style={{ color: '#9CA3AF', fontSize: 13 }}>
                                          {t('eventdashboard.team_no_members', 'No members yet.')}
                                        </div>
                                      )}
                                    </div>

                                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
                                      {isTeamFull ? (
                                        <div
                                          role="status"
                                          aria-live="polite"
                                          style={{
                                            padding: '10px 12px',
                                            borderRadius: 10,
                                            border: '1px solid #2D3748',
                                            background: 'rgba(239, 68, 68, 0.08)',
                                            color: '#FCA5A5',
                                            fontWeight: 800
                                          }}
                                        >
                                          {t('eventdashboard.team_full', 'Team full')}
                                        </div>
                                      ) : (
                                        <BracketControlSelect
                                          value={selectedAssign[team.id] || ''}
                                          onChange={(e) => setSelectedAssign((prev) => ({ ...prev, [team.id]: e.target.value }))}
                                          disabled={teamAssigning}
                                        >
                                          <option value="">{t('eventdashboard.select_approved_player', 'Select approved player...')}</option>
                                          {unassignedPlayers.map((p) => (
                                            <option key={p.id} value={p.userId}>
                                              {p.username || p.userId}
                                            </option>
                                          ))}
                                        </BracketControlSelect>
                                      )}
                                      <Button
                                        variant="primary"
                                        size="small"
                                        disabled={
                                          teamAssigning ||
                                          isTeamFull ||
                                          !selectedAssign[team.id] ||
                                          (Array.isArray(team.team) && tournamentInfo?.teamSize && team.team.length >= tournamentInfo.teamSize)
                                        }
                                        onClick={() => handleAssignToTeam(team.id)}
                                      >
                                        {t('eventdashboard.add_member', 'Add member')}
                                      </Button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ marginBottom: 18, padding: 14, border: '1px solid #2D3748', borderRadius: 12, background: 'rgba(255,255,255,0.03)' }}>
                          <h4 style={{ marginTop: 0 }}>{t('eventdashboard.teams', 'Teams')}</h4>
                          <p style={{ color: '#888' }}>
                            {t('eventdashboard.teams_disabled', 'Teams are currently')} <strong>{t('eventdashboard.disabled', 'disabled')}</strong> {t('eventdashboard.teams_disabled_suffix', 'for this tournament.')}
                          </p>
                          {(tournamentInfo?.active || tournamentInfo?.Active) && (
                            <p style={{ color: '#fbbf24' }}>
                              {t('eventdashboard.tournament_is', 'Tournament is')} <strong>{t('eventdashboard.active', 'active')}</strong>. {t('eventdashboard.reset_to_enable_teams', 'Reset it to enable teams.')}
                            </p>
                          )}
                          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                            <BracketControlInput
                              type="number"
                              min="1"
                              value={teamSizeInput}
                              onChange={(e) => setTeamSizeInput(e.target.value)}
                              style={{ width: 120 }}
                            />
                            <Button
                              variant="secondary"
                              size="small"
                              disabled={!event?.tournamentId || resettingTournament}
                              onClick={handleResetTournament}
                            >
                              {resettingTournament ? t('eventdashboard.resetting', 'Resetting...') : t('eventdashboard.reset_tournament', 'Reset tournament')}
                            </Button>
                            <Button variant="secondary" size="small" onClick={handleEnableTeams}>
                              {t('eventdashboard.enable_teams', 'Enable teams')}
                            </Button>
                          </div>
                        </div>
                      )}
                      {(tournamentPlayers || []).length === 0 ? (
                        <p style={{ color: '#888' }}>{t('eventdashboard.no_players_invite_help', 'No players yet. Use the "Players/Streamers" tab to invite them.')}</p>
                      ) : (
                        <Table>
                          <thead>
                            <tr>
                              <th>{t('common.username', 'Username')}</th>
                              <th>{t('common.name', 'Name')}</th>
                              <th>{t('common.email', 'Email')}</th>
                              <th>{t('eventdashboard.status', 'Status')}</th>
                              <th>{t('common.actions', 'Actions')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tournamentPlayers.map(player => (
                              <tr key={player.id}>
                                <td>{player.username}</td>
                                <td>{player.firstName} {player.lastName}</td>
                                <td>{player.email}</td>
                                <td><StatusBadge status={player.status}>{player.status}</StatusBadge></td>
                                <td>
                                  {player.status !== 'approved' && (
                                    <ActionButton onClick={() => handleApproveTournamentPlayer(player.id)}>
                                      {t('common.approve', 'Approve')}
                                    </ActionButton>
                                  )}
                                  <ActionButton variant="danger" onClick={() => handleRemoveTournamentPlayer(player.id)}>
                                    {t('common.remove', 'Remove')}
                                  </ActionButton>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </>
                  )}
                </Section>

                {/* Active Matches Section - Only show when tournament is running */}
                {(tournamentInfo?.Active || tournamentInfo?.active) && (
                  <Section>
                    <SectionHeader>
                      <SectionIcon>⚔️</SectionIcon>
                      <h3>{t('eventdashboard.match_management', 'Match Management')}</h3>
                    </SectionHeader>
                    {tournamentMatches.length === 0 ? (
                      <p style={{ color: '#888' }}>{t('eventdashboard.no_active_matches', 'No active matches. The bracket above shows the tournament progress.')}</p>
                    ) : (
                      <>
                        <p style={{ color: '#888', marginBottom: '12px' }}>
                          {t('eventdashboard.click_name_set_winner_prefix', 'Click on a')} {tournamentInfo?.isTeam ? t('eventdashboard.team', 'team') : t('eventdashboard.player', 'player')} {t('eventdashboard.click_name_set_winner_suffix', 'name to set them as the winner of that match.')}
                        </p>
                        <Table>
                          <thead>
                            <tr>
                              <th>{t('eventdashboard.match', 'Match')}</th>
                              <th>{tournamentInfo?.isTeam ? t('eventdashboard.team_1', 'Team 1') : t('eventdashboard.player_1', 'Player 1')}</th>
                              <th>{t('eventdashboard.vs', 'vs')}</th>
                              <th>{tournamentInfo?.isTeam ? t('eventdashboard.team_2', 'Team 2') : t('eventdashboard.player_2', 'Player 2')}</th>
                              <th>{t('eventdashboard.status', 'Status')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tournamentMatches.map((match, idx) => {
                              const isFinished = match.finished || match.Finshed;
                              const t1 = match.teams?.[0];
                              const t2 = match.teams?.[1];
                              const tbdLabel = t('eventdashboard.tbd', 'TBD');
                              const player1 = t1?.player?.name || match.player1?.username || tbdLabel;
                              const player2 = t2?.player?.name || match.player2?.username || tbdLabel;
                              const player1Id = t1?.playerID || match.player1Id || match.Player1ID;
                              const player2Id = t2?.playerID || match.player2Id || match.Player2ID;
                              const matchId = match.id || match._id;

                              // Team tournaments: show members for each team entrant
                              const isTeamTournament = !!(tournamentInfo?.isTeam || tournamentInfo?.isTeam === true);
                              const team1 = isTeamTournament
                                ? (tournamentTeams || []).find(t => String(t.id) === String(player1Id))
                                : null;
                              const team2 = isTeamTournament
                                ? (tournamentTeams || []).find(t => String(t.id) === String(player2Id))
                                : null;
                              const team1Members = Array.isArray(team1?.memberUsers)
                                ? team1.memberUsers.map(u => u?.username).filter(Boolean)
                                : [];
                              const team2Members = Array.isArray(team2?.memberUsers)
                                ? team2.memberUsers.map(u => u?.username).filter(Boolean)
                                : [];

                              return (
                                <tr key={matchId || idx}>
                                  <td>#{idx + 1}</td>
                                  <td>
                                    {!isFinished && player1 !== tbdLabel ? (
                                      <ActionButton
                                        onClick={() => handleSetWinner(matchId, player1Id, String(t1?.score ?? 1), String(t2?.score ?? 0))}
                                        disabled={settingWinner === matchId}
                                      >
                                        {settingWinner === matchId ? t('common.ellipsis', '...') : `🏆 ${player1}`}
                                      </ActionButton>
                                    ) : (
                                      <span>{player1}</span>
                                    )}
                                    {isTeamTournament && player1 !== tbdLabel && (
                                      <div style={{ marginTop: 6, color: '#9CA3AF', fontSize: 12 }}>
                                        {t('eventdashboard.members', 'Members')}: <strong style={{ color: '#E5E7EB' }}>
                                          {team1Members.length ? team1Members.join(', ') : t('eventdashboard.dash', '—')}
                                        </strong>
                                      </div>
                                    )}
                                  </td>
                                  <td style={{ textAlign: 'center' }}>{t('eventdashboard.vs', 'vs')}</td>
                                  <td>
                                    {!isFinished && player2 !== tbdLabel ? (
                                      <ActionButton
                                        onClick={() => handleSetWinner(matchId, player2Id, String(t1?.score ?? 0), String(t2?.score ?? 1))}
                                        disabled={settingWinner === matchId}
                                      >
                                        {settingWinner === matchId ? t('common.ellipsis', '...') : `🏆 ${player2}`}
                                      </ActionButton>
                                    ) : (
                                      <span>{player2}</span>
                                    )}
                                    {isTeamTournament && player2 !== tbdLabel && (
                                      <div style={{ marginTop: 6, color: '#9CA3AF', fontSize: 12 }}>
                                        {t('eventdashboard.members', 'Members')}: <strong style={{ color: '#E5E7EB' }}>
                                          {team2Members.length ? team2Members.join(', ') : t('eventdashboard.dash', '—')}
                                        </strong>
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    <StatusBadge status={isFinished ? 'completed' : 'pending'}>
                                      {isFinished ? t('eventdashboard.completed', '✅ Completed') : t('eventdashboard.pending', '⏳ Pending')}
                                    </StatusBadge>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </>
                    )}
                  </Section>
                )}
              </>
            ) : (
              <p style={{ color: '#fbbf24' }}>
                {t('eventdashboard.no_linked_tournament_help', 'No linked tournament found yet. Publish the event again or contact support if this persists.')}
              </p>
            )}
          </div>
        )}

        {/* Stream Control Tab - For Stream and Workshop events */}
        {activeTab === 'stream' && (isStream || isWorkshop) && (
          <div>
            <h3>{t('eventdashboard.stream_control_title', '🎥 Stream Control')}</h3>
            <p style={{ color: '#888', marginBottom: '20px' }}>
              {isWorkshop
                ? t('eventdashboard.workshop_stream_help', 'As the workshop organizer, you are the main presenter. Use the controls below to manage your stream.')
                : t('eventdashboard.stream_organizer_help', 'As the stream organizer, you control the broadcast. Configure your stream settings below.')}
            </p>

            <Section>
              <SectionHeader>
                <SectionIcon>📡</SectionIcon>
                <h3>{t('eventdashboard.stream_status', 'Stream Status')}</h3>
              </SectionHeader>
              <StreamStatusBox>
                <StatusIndicator isLive={event.status === 'active'}>
                  {event.status === 'active' ? t('eventdashboard.live', '🔴 LIVE') : t('eventdashboard.offline', '⚫ OFFLINE')}
                </StatusIndicator>
                <Button
                  onClick={() => setShowGoLiveModal(true)}
                  style={{ marginTop: '1rem', background: '#667eea', fontWeight: 600 }}
                >
                  {event.status === 'active'
                    ? t('eventdashboard.manage_live_stream', '📺 Manage Live Stream')
                    : t('eventdashboard.go_live', '🚀 Go Live')}
                </Button>
              </StreamStatusBox>
            </Section>

            <Section>
              <SectionHeader>
                <SectionIcon>⚙️</SectionIcon>
                <h3>{t('eventdashboard.stream_configuration', 'Stream Configuration')}</h3>
              </SectionHeader>
              <StreamConfigForm>
                <ConfigItem>
                  <ConfigLabel>
                    <input
                      type="checkbox"
                      checked={event.options?.restream || false}
                      onChange={(e) => handleUpdateEvent({
                        options: { ...event.options, restream: e.target.checked }
                      })}
                    />
                    <span>📡 {t('createevent.enable_restream', 'Enable Restream')}</span>
                  </ConfigLabel>
                  <ConfigDescription>
                    {t('eventdashboard.restream_help', 'Simultaneously stream to your configured platforms (Twitch, YouTube, Facebook)')}
                  </ConfigDescription>
                </ConfigItem>

                <ConfigItem>
                  <ConfigLabel>
                    <input
                      type="checkbox"
                      checked={event.options?.autoReplay || false}
                      onChange={(e) => handleUpdateEvent({
                        options: { ...event.options, autoReplay: e.target.checked }
                      })}
                    />
                    <span>🎬 {t('createevent.auto_generate_replay', 'Auto-Generate Replay')}</span>
                  </ConfigLabel>
                  <ConfigDescription>
                    {t('eventdashboard.auto_replay_help', 'Automatically save and make the stream available as a replay after it ends')}
                  </ConfigDescription>
                </ConfigItem>

                {isStream && (
                  <>
                    <ConfigItem>
                      <ConfigLabel>{t('createevent.chat_mode', 'Chat Mode')}</ConfigLabel>
                      <ThemedSelect
                        value={event.options?.chatMode || 'public'}
                        onChange={(e) => handleUpdateEvent({
                          options: { ...event.options, chatMode: e.target.value }
                        })}
                        style={{ marginTop: '0.5rem' }}
                      >
                        <option value="public">💬 {t('createevent.chat_mode_public', 'Public')}</option>
                        <option value="subscribers">👑 {t('createevent.chat_mode_subscribers', 'Subscribers Only')}</option>
                        <option value="disabled">🚫 {t('createevent.chat_mode_disabled', 'Disabled')}</option>
                      </ThemedSelect>
                      <ConfigDescription>
                        {t('eventdashboard.chat_help', 'Control who can participate in the chat')}
                      </ConfigDescription>
                    </ConfigItem>
                  </>
                )}
              </StreamConfigForm>
            </Section>

            {isWorkshop && (
              <Section>
                <SectionHeader>
                  <SectionIcon>👥</SectionIcon>
                  <h3>{t('eventdashboard.workshop_capacity', 'Workshop Capacity')}</h3>
                </SectionHeader>
                <p style={{ color: '#888' }}>
                  {t('eventdashboard.current_attendees', 'Current Attendees')}: <strong>{viewers.length}</strong> / {event.maxParticipants || '∞'}
                </p>
                {event.maxParticipants && viewers.length >= event.maxParticipants && (
                  <p style={{ color: '#EF4444', marginTop: '0.5rem' }}>
                    ⚠️ {t('eventdashboard.workshop_full', 'Workshop is at full capacity')}
                  </p>
                )}
              </Section>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <EventSettings
            event={event}
            onUpdate={handleUpdateEvent}
            onBan={handleBanUser}
            ticketStats={ticketStats}
            generatingCodes={generatingCodes}
            generatedCodes={generatedCodes}
            issuedCodes={issuedCodes}
            onGenerateCodes={handleGenerateTicketCodes}
            isTournament={isTournament}
            hasTournamentId={!!event?.tournamentId}
            onResetTournament={handleResetTournament}
            resettingTournament={resettingTournament}
            onDeleteEvent={handleDeleteEvent}
            deletingEvent={deletingEvent}
          />
        )}
      </ContentCard>

      {/* Go Live Modal for Stream/Workshop */}
      {showGoLiveModal && (
        <GoLiveModalOverlay onClick={() => setShowGoLiveModal(false)}>
          <GoLiveModalContent onClick={(e) => e.stopPropagation()}>
            <StartStreamV2
              eventSlug={slug}
              hideModal={() => setShowGoLiveModal(false)}
              serverIP="rtmp://your-server.com/live"
              streamKey="your-stream-key"
            />
          </GoLiveModalContent>
        </GoLiveModalOverlay>
      )}
    </DashboardContainer>
  );
};

// Settings Component
const EventSettings = ({
  event,
  onUpdate,
  onBan,
  ticketStats,
  generatingCodes,
  generatedCodes,
  issuedCodes,
  onGenerateCodes,
  isTournament,
  hasTournamentId,
  onResetTournament,
  resettingTournament,
  onDeleteEvent,
  deletingEvent
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: event.name,
    description: event.description,
    ticketPrice: event.ticketPrice,
    status: event.status,
    maxParticipants: event.maxParticipants
  });

  const [uploadingBanner, setUploadingBanner] = useState(false);

  const uploadImage = async (type, file) => {
    if (!file) return;
    const endpoint = `${ENV_CONFIG.EVENTS_ENDPOINT}/events/${event.slug}/banner`;

    const fd = new FormData();
    fd.append('filename', file);

    setUploadingBanner(true);

    try {
      const res = await axios.post(endpoint, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(t('eventdashboard.banner_image_updated', 'Banner image updated'));
      // Refresh page state by reloading (simple, avoids prop drilling)
      window.location.reload();
      return res.data;
    } catch (err) {
      console.error('❌ Upload failed:', err);
      toast.error(err.response?.data?.message || t('eventdashboard.upload_failed', 'Upload failed'));
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(formData);
  };

  return (
    <div>
      <h3>{t('eventdashboard.event_settings', 'Event Settings')}</h3>

      <div style={{ marginBottom: 24, paddingBottom: 18, borderBottom: '1px solid #333' }}>
        <h4 style={{ marginTop: 0 }}>{t('eventdashboard.images', 'Images')}</h4>
        <p style={{ color: '#888', fontSize: '0.9rem' }}>
          {t('eventdashboard.upload_banner_help_prefix', 'Upload a')} <strong>{t('eventdashboard.banner', 'banner')}</strong> {t('eventdashboard.upload_banner_help_suffix', 'for your tournament. It will be used on the event details page and tournament listings.')}
        </p>

        <div style={{ display: 'grid', gap: 14 }}>
          <div>
            <div style={{ color: '#AAA', fontSize: '0.9rem', marginBottom: 6 }}>{t('eventdashboard.banner_image', 'Banner image')}</div>
            {event.bannerImage && (
              <img
                src={event.bannerImage}
                alt={t('eventdashboard.event_banner_alt', 'Event banner')}
                style={{ width: '100%', maxWidth: 520, borderRadius: 10, border: '1px solid #333', marginBottom: 10 }}
              />
            )}
            <input
              type="file"
              accept="image/*"
              disabled={uploadingBanner}
              onChange={(e) => uploadImage('banner', e.target.files?.[0])}
            />
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <FieldGroup label={t('eventdashboard.event_name', 'Event Name')}>
          <InputGroup
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
          />
        </FieldGroup>

        <FieldGroup label={t('event.description', 'Description')}>
          <TextareaGroup
            value={formData.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
            minRows={5}
          />
        </FieldGroup>

        <FieldGroup label={t('eventdashboard.ticket_price_gcoins', 'Ticket Price (G-Coins)')}>
          <InputGroup
            type="number"
            value={formData.ticketPrice}
            onChange={e => setFormData({ ...formData, ticketPrice: Number(e.target.value) })}
          />
        </FieldGroup>

        <FieldGroup label={t('eventdashboard.status', 'Status')}>
          <ThemedSelect
            value={formData.status}
            onChange={e => setFormData({ ...formData, status: e.target.value })}
          >
            <option value="draft">{t('common.draft', 'Draft')}</option>
            <option value="published">{t('common.published', 'Published')}</option>
            <option value="active">{t('common.active', 'Active')}</option>
            <option value="completed">{t('common.completed', 'Completed')}</option>
            <option value="cancelled">{t('common.cancelled', 'Cancelled')}</option>
          </ThemedSelect>
        </FieldGroup>

        <FieldGroup label={t('eventdashboard.max_participants', 'Max Participants')}>
          <InputGroup
            type="number"
            value={formData.maxParticipants}
            onChange={e => setFormData({ ...formData, maxParticipants: Number(e.target.value) })}
          />
        </FieldGroup>

        <Button type="submit">{t('common.save_changes', 'Save Changes')}</Button>
      </form>

      <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #333' }}>
        <h4 style={{ color: '#EF4444' }}>{t('eventdashboard.danger_zone', 'Danger Zone')}</h4>

        {isTournament && (
          <div style={{ marginBottom: 16 }}>
            <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
              {t(
                'eventdashboard.reset_tournament_help',
                'Need to restart the bracket without deleting the event? Use reset.'
              )}
            </p>
            <Button
              type="button"
              disabled={!hasTournamentId || resettingTournament}
              onClick={onResetTournament}
              style={{ background: '#374151', fontWeight: 700 }}
            >
              {resettingTournament
                ? t('eventdashboard.resetting', 'Resetting...')
                : t('eventdashboard.reset_tournament', 'Reset tournament')}
            </Button>
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
            {t(
              'eventdashboard.delete_event_help',
              'Delete the event to start over. If this is a tournament, the linked tournament will be archived as well.'
            )}
          </p>
          <Button
            type="button"
            disabled={deletingEvent}
            onClick={onDeleteEvent}
            style={{ background: '#EF4444', fontWeight: 800 }}
          >
            {deletingEvent
              ? t('eventdashboard.deleting', 'Deleting...')
              : t(
                isTournament ? 'eventdashboard.delete_tournament' : 'eventdashboard.delete_event',
                isTournament ? 'Delete tournament' : 'Delete event'
              )}
          </Button>
        </div>
      </div>

      {event.ticketPrice > 0 && (
        <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #333' }}>
          <h4 style={{ color: '#FBBF24' }}>{t('eventdashboard.tickets_access', 'Tickets & Access')}</h4>
          <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1rem' }}>
            {t('eventdashboard.ticket_codes_help', 'Generate ticket codes you can distribute to viewers, sponsors or partners. Each code can be used once to get access to this event.')}
          </p>

          <p style={{ color: '#AAA', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
            {t('eventdashboard.ticket_price', 'Ticket price')}: <strong>{event.ticketPrice} {t('eventdashboard.gcoins', 'G‑Coins')}</strong>
          </p>

          {ticketStats && (
            <p style={{ color: '#AAA', fontSize: '0.9rem', marginBottom: '1rem' }}>
              {t('eventdashboard.tickets_issued', 'Tickets issued')}: <strong>{ticketStats.total}</strong> • {t('eventdashboard.used', 'Used')}: <strong>{ticketStats.validated}</strong> • {t('eventdashboard.pending_plain', 'Pending')}: <strong>{ticketStats.pending}</strong>
            </p>
          )}

          <Button
            type="button"
            disabled={generatingCodes}
            onClick={onGenerateCodes}
          >
            {generatingCodes ? t('eventdashboard.generating_codes', 'Generating codes...') : t('eventdashboard.generate_ticket_codes', 'Generate Ticket Codes')}
          </Button>

          {generatedCodes && generatedCodes.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                {t('eventdashboard.new_codes_help', 'Newly generated codes (copy and share them with your viewers):')}
              </p>
              <ul style={{ listStyle: 'none', paddingLeft: 0, maxHeight: '200px', overflowY: 'auto', fontSize: '0.85rem' }}>
                {generatedCodes.map(code => (
                  <li key={code.id} style={{ marginBottom: '4px' }}>
                    <code style={{ background: '#111827', padding: '4px 8px', borderRadius: '4px' }}>
                      {code.code}
                    </code>
                    <span style={{ color: '#6B7280', marginLeft: '8px' }}>
                      {new Date(code.created_at).toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {issuedCodes && issuedCodes.length > 0 && (
            <div style={{ marginTop: '1.25rem' }}>
              <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                {t('eventdashboard.all_issued_codes', 'All issued codes (persisted):')}
              </p>
              <ul style={{ listStyle: 'none', paddingLeft: 0, maxHeight: '260px', overflowY: 'auto', fontSize: '0.85rem' }}>
                {issuedCodes.map(code => (
                  <li key={code.id} style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <code style={{ background: '#111827', padding: '4px 8px', borderRadius: '4px' }}>
                      {code.code}
                    </code>
                    <span style={{ color: code.is_validated ? '#10B981' : '#F59E0B', fontWeight: 700 }}>
                      {code.is_validated ? t('eventdashboard.used_upper', 'USED') : t('eventdashboard.pending_upper', 'PENDING')}
                    </span>
                    <span style={{ color: '#6B7280' }}>
                      {code.created_at ? new Date(code.created_at).toLocaleString() : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Styled Components for Overview
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatBox = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 1.5rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
  }
`;

const StatIcon = styled.div`
  font-size: 2.5rem;
  line-height: 1;
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: white;
  line-height: 1;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Section = styled.div`
  background: ${({ theme }) => theme.cardBg};
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid ${({ theme }) => theme.borderColor};

  h3 {
    margin: 0;
    color: ${({ theme }) => theme.textColor};
    font-size: 1.25rem;
  }
`;

const SectionIcon = styled.span`
  font-size: 1.5rem;
`;

const DetailGrid = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const DetailItem = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 1rem;
  padding: 1rem;
  background: ${({ theme }) => theme.body};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.borderColor};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DetailLabel = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DetailValue = styled.div`
  color: ${({ theme }) => theme.textColor};
  font-size: 1rem;
  line-height: 1.6;
`;

const CategoryBadge = styled.span`
  display: inline-block;
  padding: 0.5rem 1rem;
  background: ${props => props.color || '#4299e1'};
  color: white;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.875rem;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Tag = styled.span`
  display: inline-block;
  padding: 0.375rem 0.75rem;
  background: ${({ theme }) => theme.cardBg};
  color: ${({ theme }) => theme.textSecondary};
  border-radius: 6px;
  font-size: 0.875rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
`;

const OptionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: ${props => props.enabled ? '#1e4620' : props.theme.cardBg};
  border: 1px solid ${props => props.enabled ? '#38a169' : props.theme.borderColor};
  border-radius: 8px;
`;

const OptionIcon = styled.span`
  font-size: 1.5rem;
`;

const OptionText = styled.span`
  color: ${({ theme }) => theme.textColor};
  font-weight: 500;
`;

const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const QuickActionButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1.5rem;
  background: ${({ theme }) => theme.cardBg};
  border: 2px solid ${({ theme }) => theme.borderColor};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  color: ${({ theme }) => theme.textColor};

  &:hover {
    background: ${({ theme }) => theme.hoverBg};
    border-color: ${({ theme }) => theme.color.primary};
    transform: translateY(-2px);
  }
`;

const ActionIcon = styled.span`
  font-size: 2rem;
`;

const ActionText = styled.span`
  font-weight: 600;
  font-size: 0.875rem;
  text-align: center;
`;

// Stream Control Styled Components
const StreamStatusBox = styled.div`
  text-align: center;
  padding: 2rem;
  background: ${({ theme }) => theme.body};
  border-radius: 12px;
  border: 2px solid ${({ theme }) => theme.borderColor};
`;

const StatusIndicator = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.isLive ? '#EF4444' : '#888'};
  margin-bottom: 1rem;
  animation: ${props => props.isLive ? 'pulse 2s infinite' : 'none'};

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }
`;

const StreamConfigForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ConfigItem = styled.div`
  padding: 1rem;
  background: ${({ theme }) => theme.body};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.borderColor};
`;

const ConfigLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.textColor};
  font-size: 1rem;
  cursor: pointer;

  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    cursor: pointer;
  }

  span {
    user-select: none;
  }
`;

const ConfigDescription = styled.p`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.875rem;
  margin: 0.5rem 0 0 2rem;
  line-height: 1.5;
`;

// Go Live Modal Styled Components
const GoLiveModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const GoLiveModalContent = styled.div`
  background: ${({ theme }) => theme.cardBg};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 12px;
  padding: 30px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`;

export default EventDashboard;

